import HomeTableSkeleton from "src/components/common/HomeTableSkeleton";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { AxiosError, AxiosResponse } from "axios";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  createSearchParams,
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import DataGrid from "src/components/common/DataGrid";
import EmptyView from "src/components/common/EmptyView";
import { Button } from "src/components/common/FormComponents/Buttons";
import HorizontalTabs from "src/components/common/HorizontalTabs";
import {
  fundActionByStatus,
  fundPaymentTabs,
  fundStatus,
} from "src/constants/common";
import { archiveFundRequest, getFundRequest } from "src/utils/api/dashboard";
import {
  dateAndTimeStamp,
  downloadSheet,
  formatINR,
  generateExportFilename,
  getFinancialYear,
} from "src/utils/helper";
import defaultStyles, {
  StylesClasses,
  toolbarButtonStyles,
  toolbarFieldStyles,
} from "./styles";
import { path } from "src/constants/path";
import SearchBar from "src/components/common/SearchField";
import { useAppSelector } from "src/redux/store";
import MultiSelect from "src/components/common/MultiSelect";
import { getStates } from "src/utils/api/roles";
import { getStyles } from "src/styles/theme";
import { usePersistedFilters } from "src/hooks/usePersistedFilters";
import { useRecordEntityOptions } from "src/hooks/useRecordEntityOptions";
import { FUND_SECTIONS } from "../ProjectDetailView/FundApplication/FundApplicationUI";

/** Which installment of an EPID's fund applications a row is: FA1, FA2, ... */
const faTerm = (yearOfFundApplication?: number) =>
  yearOfFundApplication ? `FA${yearOfFundApplication}` : "-";

const FundApplication = () => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();

  const tab = searchParams.get("currentTab");

  const { persisted, saveFilters } = usePersistedFilters("fundApplication");

  const [tableRows, setTableRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [activeTab, setActiveTab] = useState(
    tab ?? persisted?.activeTab ?? undefined
  );

  const [activeYearTab, setActiveYearTab] = useState<number | undefined>(
    undefined
  );

  const [states, setStates] = useState<any>([]);

  const [archiveRow, setArchiveRow] = useState<any>(null);

  const [isExporting, setIsExporting] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 50,
    page: 0,
  });

  const user = useAppSelector((state) => state.root.user);

  const styles = getStyles<StylesClasses>(defaultStyles);

  const fundYearTabs = [
    { label: "All", value: undefined },
    { label: "FA 1", value: 1 },
    { label: "FA 2", value: 2 },
    { label: "FA 3", value: 3 },
    { label: "FA 4", value: 4 },
    { label: "FA 5", value: 5 },
    { label: "FA 6", value: 6 },
    { label: "FA 7", value: 7 },
    { label: "FA 8", value: 8 },
    { label: "FA 9", value: 9 },
    { label: "FA 10", value: 10 },
  ];

  const [queryParams, setQueryParams] = useState<{
    status: string;
    offset: number;
    limit: number;
    download: boolean;
    sortBy: string;
    searchText: string;
    projectStateIds?: string[];
    yearOfFundApplication?: number;
    entityIds?: string[];
  }>({
    status: activeTab,
    offset: persisted?.queryParams?.offset ?? 0,
    limit: 50,
    download: false,
    sortBy: persisted?.queryParams?.sortBy ?? "updated_at:DESC",
    searchText: persisted?.queryParams?.searchText ?? "",
    projectStateIds: persisted?.queryParams?.projectStateIds ?? undefined,
    yearOfFundApplication: undefined,
    entityIds: persisted?.queryParams?.entityIds ?? undefined,
  });

  const { entityInfo } = useRecordEntityOptions("fund", {
    status: queryParams.status,
    projectStateIds: queryParams.projectStateIds?.join(","),
    yearOfFundApplication: queryParams.yearOfFundApplication,
    searchText: queryParams.searchText,
  });

  const [filterKey, setFilterKey] = useState(
    persisted?.filterKey ?? { projectStateIds: null }
  );

  // SearchBar re-invokes getText on every render (its debounce effect depends
  // on the inline callback), so we track the last applied search term and only
  // reset pagination when the term actually changes — otherwise paging would be
  // snapped back to page 1 by the repeated same-value calls.
  const lastSearchRef = useRef<string>(
    persisted?.queryParams?.searchText ?? ""
  );

  useEffect(() => {
    const { download, ...qp } = queryParams;
    saveFilters({ queryParams: qp, filterKey, activeTab });
  }, [queryParams, filterKey, activeTab]);

  const { isLoading, isFetching } = useQuery(
    ["users", queryParams],
    () =>
      getFundRequest({
        ...queryParams,
      }),
    {
      onSuccess: (res: AxiosResponse) => {
        const row = res?.data?.rows.map((row) => ({
          sno: row?.serialNo,
          id: row.id,
          costEstimateID: row.landOwnershipEpid,
          registrationNo: row.landOwnership.projectDetail.registrationNo,
          entityName: row.landOwnership.user.firstName,
          state: row.landOwnership?.projectDetail?.address?.stateDetail?.name,
          division: row.landOwnership.projectDetail.landSpecification.division,
          allocateDivision:
            row.landOwnership.projectDetail.organizationAffiliationDetail
              ?.userDetail?.division,
          totalArea: `${row.landOwnership.netPlantationArea}`,
          ecoRestorationAmount: formatINR(
            row?.landOwnership?.demandNote ?? "-"
          ),
          totalAmountReleasedRaw: row.totalAmountReleased,
          totalCostRaw: row.totalCost,
          financialProgress: row.financialProgress,

          estimatedAdvanceWorkCost: formatINR(row.estimatedAdvanceWorkCost),
          estimatedRaisingCost: formatINR(row.estimatedRaisingCost),
          estimatedMaintenanceCost: formatINR(row.estimatedMaintenanceCost),
          estimatedMonitoringCost: formatINR(row.estimatedMonitoringCost),
          estimatedContingencyCost: formatINR(row.estimatedContingencyCost),
          totalEstimatedCost: formatINR(row.totalEstimatedCost),

          advanceWorkCost: formatINR(row.advanceWorkCost),
          raisingCost: formatINR(row.raisingCost),
          maintenanceCost: formatINR(row.maintenanceCost),
          monitoringCost: formatINR(row.monitoringCost),
          contingencyCost: formatINR(row.contingencyCost),
          totalCost: formatINR(row.totalCost),
          totalAmountReleased: formatINR(row.totalAmountReleased),
          // Total Balance = Eco-Restoration Cost - cumulative amount released
          // across this EPID's FAs up to and including this FA year.
          totalBalance: formatINR(
            Number(row?.landOwnership?.demandNote ?? 0) -
              Number(row?.cumulativeAmountReleased ?? 0)
          ),
          status: fundStatus[row.status] ?? row.status,
          rawStatus: row.status,
          ecoRestorationRaw: Number(row?.landOwnership?.demandNote ?? 0),
          newKmlStatus: row.newKmlStatus ?? null,
          utilizationCertificateDocId: row.utilizationCertificateDocId ?? null,

          created_at: dateAndTimeStamp(row.created_at, "IST"),
          updated_at: dateAndTimeStamp(row.updated_at, "IST"),
          // FY of the payment release (fund_detail.released_date), so it stays
          // "-" until an application's funds are actually released.
          releaseFinancialYear: getFinancialYear(row.releasedDate),

          remarks: row.remark,
          isLatestFundApplication: row?.isLatestFundApplication,
          yearOfFundApplication: row?.yearOfFundApplication,
        }));

        if (queryParams.download) {
          const exportData = row.map((item) => {
            return {
              "S.No": item?.sno,
              "FA-ID": item?.id,
              "FA Term": faTerm(item?.yearOfFundApplication),
              "Registration No": item?.registrationNo,
              "Entity Name": item.entityName,
              State: item?.state,
              Division: item?.division,
              "Allocate Division": item?.allocateDivision,
              "Net Planned Area (Ha)": item?.totalArea,
              "Eco-Restoration Cost (₹)": item?.ecoRestorationAmount,
              "Est.Advance Work Cost (₹)": item?.estimatedAdvanceWorkCost,
              "Est.Raising Cost (₹)": item?.estimatedRaisingCost,
              "Est.Maintenance Cost (₹)": item?.estimatedMaintenanceCost,
              "Est.Monitoring Cost (₹)": item?.estimatedMonitoringCost,
              "Est.Contingency Cost (₹)": item?.estimatedContingencyCost,
              "Est.Total Cost (₹)": item?.totalEstimatedCost,
              "Advance Work Cost (₹)": item?.advanceWorkCost,
              "Raising Cost (₹)": item?.raisingCost,
              "Maintenance Cost (₹)": item?.maintenanceCost,
              "Monitoring Cost (₹)": item?.monitoringCost,
              "Contingency Cost (₹)": item?.contingencyCost,
              "Total Cost (₹)": item?.totalCost,
              "Total Balance (₹)": item?.totalBalance,
              Status: item?.status,
              "Created At": item?.created_at,
              "Updated At": item?.updated_at,
              "Financial Year": item?.releaseFinancialYear,
              Remark: item?.remarks,
            };
          });
          downloadSheet(exportData);
          setQueryParams((prev) => ({
            ...prev,
            download: false,
          }));
        } else {
          setTableRows(row);
          setTotalRecords(res?.data?.count ?? 0);
        }
      },
      onError: (err: AxiosError) => {},
      refetchOnMount: true,
    }
  );

  useQuery(["getStatesType"], () => getStates(), {
    onSuccess: (data) => {
      setStates(data.data);
    },
    refetchOnMount: true,
  });

  const { mutate: archiveFa, isLoading: isArchiving } = useMutation(
    (id: string) => archiveFundRequest(id),
    {
      onSuccess: () => {
        setArchiveRow(null);
        // Force a refetch so the archived row drops out immediately.
        // (Spreading queryParams keeps the same react-query key hash, so it
        // would NOT trigger a refetch on its own.)
        queryClient.invalidateQueries(["users"]);
      },
    }
  );

  const RenderView = (params) => {
    const faYear = params?.row?.yearOfFundApplication;
    const isLatestFa = params?.row?.isLatestFundApplication;
    // Show the FA number badge under every tab (year + status). The latest FA
    // is highlighted in purple, older ones in orange.
    const showFaBadge = !!faYear;
    const badgeColor = isLatestFa ? "#8E44AD" : "#E67E22";
    const badgeBg = isLatestFa
      ? "rgba(142, 68, 173, 0.12)"
      : "rgba(230, 126, 34, 0.12)";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Box
          component={RouterLink}
          to={`${"/fund-application-details"}?${createSearchParams({
            id: params.row.id,
          })}`}
          sx={{ ...defaultStyles.renderButtonText2 }}
        >{`${params?.row?.id}`}</Box>
        {showFaBadge && (
          <Box
            component="span"
            sx={{
              color: badgeColor,
              backgroundColor: badgeBg,
              fontWeight: 700,
              fontSize: "11px",
              lineHeight: 1,
              padding: "3px 6px",
              borderRadius: "4px",
              fontStyle: "italic",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {`FA${faYear}`}
          </Box>
        )}
      </Box>
    );
  };

  // Custom-rendered cells inherit the grid's 14px, unlike plain columns which
  // pick up .MuiDataGrid-cellContent — so size/weight are set here to keep the
  // EPID as prominent as the Status beside it.
  const RenderEPID = (params) => {
    return (
      <Box sx={{ fontSize: "15px", fontWeight: 600 }}>
        {`${params?.row?.costEstimateID}`}
      </Box>
    );
  };

  const RenderUploadUc = (params) => {
    const docKey = params?.row?.utilizationCertificateDocId;
    const rowStatus = params?.row?.rawStatus;
    const uploaded = !!docKey;
    const isApproved = rowStatus === "UC_APPROVED";
    const isRejected = rowStatus === "UC_REJECTED";

    const label = isApproved
      ? "View UC"
      : isRejected
      ? "Re-upload UC"
      : uploaded
      ? "View / Change"
      : "Upload UC";

    return (
      <Box
        component="span"
        onClick={() =>
          // The utilization certificate form lives on the FA details page.
          navigate(
            `/fund-application-details?${createSearchParams({
              id: params?.row?.id,
              section: FUND_SECTIONS.uc,
            })}`
          )
        }
        sx={{
          display: "inline-block",
          px: 1.2,
          py: 0.5,
          borderRadius: "14px",
          fontSize: "12px",
          fontWeight: 600,
          whiteSpace: "nowrap",
          cursor: "pointer",
          backgroundColor: isApproved
            ? "#E8F5E9"
            : isRejected
            ? "#FDECEA"
            : uploaded
            ? "#E8F5E9"
            : "#1B7A6E",
          color: isApproved
            ? "#1B7A6E"
            : isRejected
            ? "#C62828"
            : uploaded
            ? "#2E7D32"
            : "#fff",
        }}
      >
        {label}
      </Box>
    );
  };

  // Where the application currently stands — a plain label, not a control.
  // Anything actionable lives in the Action column next to it.
  const RenderStatus = (params) => {
    const status = params?.row?.rawStatus;

    return (
      <Box
        sx={{
          color: "text.primary",
          fontWeight: 500,
          whiteSpace: "normal",
          lineHeight: 1.3,
        }}
      >
        {fundStatus[status] ?? status ?? "--"}
      </Box>
    );
  };

  // What this user can do about that status. Admin only ever owns two steps of
  // the flow — releasing the funds and verifying the UC — so every other status
  // leaves the cell blank here, with the action showing up in the user portal
  // for the SNO / IO instead.
  const RenderAction = (params) => {
    const status = params?.row?.rawStatus;
    const action = fundActionByStatus[status];

    if (!action || action.actor !== "ADMIN") {
      return <Box sx={{ color: "text.disabled" }}>--</Box>;
    }

    return (
      <Box
        sx={{
          color: "#16A085",
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "normal",
          lineHeight: 1.3,
          "&:hover": { textDecoration: "underline" },
        }}
        onClick={() => {
          // Both admin actions live on the details page: Release Fund on the
          // release form, Verify UC on the accept / reject panel.
          navigate(
            `/fund-application-details?${createSearchParams({
              id: params.row.id,
              section:
                status === "RAISED"
                  ? FUND_SECTIONS.release
                  : FUND_SECTIONS.verifyUc,
            })}`
          );
        }}
      >
        {action.label}
      </Box>
    );
  };

  const tableColumns: any = [
    {
      field: "sno",
      headerName: "S.No",
      width: 70,
      sortable: false,
    },
    {
      field: "id",
      headerName: "Fund App ID",
      width: 230,
      sortable: false,
      renderCell: RenderView,
    },
    {
      field: "registrationNo",
      headerName: "Reg.No",
      width: 100,
      sortable: true,
    },
    {
      field: "status",
      headerName: "Status",
      width: 180,
      sortable: false,
      renderCell: RenderStatus,
    },
    {
      field: "action",
      headerName: "Action",
      width: 180,
      sortable: false,
      renderCell: RenderAction,
    },
    {
      field: "costEstimateID",
      headerName: "Cost Estimate ID",
      width: 220,
      sortable: false,
      renderCell: RenderEPID,
    },
    // Upload UC is relevant once payment is released and through the UC review
    // lifecycle — show it on the "Payment Released" / "Payment Receipt Uploaded"
    // tabs and the UC tabs.
    ...(activeTab === "APPROVED" ||
    activeTab === "PAYMENT_ACKNOWLEDGED" ||
    activeTab === "UC_UPLOADED" ||
    activeTab === "UC_APPROVED" ||
    activeTab === "UC_REJECTED"
      ? [
          {
            field: "utilizationCertificateDocId",
            headerName: "Upload UC",
            width: 150,
            sortable: false,
            renderCell: RenderUploadUc,
          },
        ]
      : []),
    {
      field: "state",
      headerName: "State",
      width: 150,
      sortable: true,
    },
    // {
    //   field: "division",
    //   headerName: "Division",
    //   width: 150,
    //   sortable: true,
    //   userType: ["INDIVIDUAL"],
    //   tab: [],
    // },
    {
      field: "allocateDivision",
      headerName: "Forest Division",
      width: 150,
      sortable: false,
    },
    {
      field: "releaseFinancialYear",
      headerName: "Financial Year",
      width: 140,
      // Derived client-side from released_date; the API can only sort on real
      // columns, so leave sorting off rather than send it a field it lacks.
      sortable: false,
    },
    {
      field: "created_at",
      headerName: "Created At",
      width: 150,
      sortable: true,
    },
    {
      field: "updated_at",
      headerName: "UPDATED AT",
      width: 150,
      sortable: true,
    },
    {
      field: "entityName",
      headerName: "Entity Name",
      width: 150,
      sortable: true,
    },
    // {
    //   field: "totalArea",
    //   headerName: "Net Planned Area(Ha)",
    //   width: 150,
    //   sortable: false,
    // },
    {
      field: "ecoRestorationAmount",
      headerName: "Eco-Restoration Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "estimatedRaisingCost",
      headerName: "Est.Raising Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "estimatedMaintenanceCost",
      headerName: "Est.Maintenance Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "raisingCost",
      headerName: "Raising Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "maintenanceCost",
      headerName: "Maintenance Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "contingencyCost",
      headerName: "Contingency Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "totalCost",
      headerName: "Total Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "totalAmountReleased",
      headerName: "Total Amount Released(₹)",
      width: 200,
      sortable: true,
      userType: ["INDIVIDUAL", null],
      tab: [],
    },
    {
      field: "totalBalance",
      headerName: "Total Balance(₹)",
      width: 200,
      sortable: false,
    },
    ...(user?.data?.role !== "Approver-view-only"
      ? [
          {
            field: "actions",
            headerName: "Actions",
            width: 100,
            sortable: false,
            renderCell: (params: any) => (
              <Tooltip title="Reject and Archive">
                <IconButton
                  size="small"
                  onClick={() => setArchiveRow(params.row)}
                  sx={{ color: "error.main" }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  const exportToExcelEntity = async () => {
    setIsExporting(true);
    try {
      const response = await getFundRequest({
        ...queryParams,
        offset: 0,
        limit: totalRecords || 10000,
        download: false,
      });
      const allRows = response?.data?.rows?.map((row) => ({
        sno: row?.serialNo,
        id: row.id,
        costEstimateID: row.landOwnershipEpid,
        registrationNo: row.landOwnership.projectDetail.registrationNo,
        entityName: row.landOwnership.user.firstName,
        state: row.landOwnership?.projectDetail?.address?.stateDetail?.name,
        division: row.landOwnership.projectDetail.landSpecification.division,
        allocateDivision:
          row.landOwnership.projectDetail.organizationAffiliationDetail
            ?.userDetail?.division,
        totalArea: `${row.landOwnership.netPlantationArea}`,
        ecoRestorationAmount: formatINR(row?.landOwnership?.demandNote ?? "-"),
        estimatedAdvanceWorkCost: formatINR(row.estimatedAdvanceWorkCost),
        estimatedRaisingCost: formatINR(row.estimatedRaisingCost),
        estimatedMaintenanceCost: formatINR(row.estimatedMaintenanceCost),
        estimatedMonitoringCost: formatINR(row.estimatedMonitoringCost),
        estimatedContingencyCost: formatINR(row.estimatedContingencyCost),
        totalEstimatedCost: formatINR(row.totalEstimatedCost),
        advanceWorkCost: formatINR(row.advanceWorkCost),
        raisingCost: formatINR(row.raisingCost),
        maintenanceCost: formatINR(row.maintenanceCost),
        monitoringCost: formatINR(row.monitoringCost),
        contingencyCost: formatINR(row.contingencyCost),
        totalCost: formatINR(row.totalCost),
        totalAmountReleased: formatINR(row.totalAmountReleased),
        totalBalance: formatINR(
          Number(row?.landOwnership?.demandNote ?? 0) -
            Number(row?.cumulativeAmountReleased ?? 0)
        ),
        status: fundStatus[row.status] ?? "-",
        created_at: dateAndTimeStamp(row.created_at, "IST"),
        updated_at: dateAndTimeStamp(row.updated_at, "IST"),
        releaseFinancialYear: getFinancialYear(row.releasedDate),
        remarks: row.remark,
        yearOfFundApplication: row?.yearOfFundApplication,
      }));
      const exportData = allRows.map((item) => ({
        "S.No": item?.sno,
        "FA-ID": item?.id,
        "FA Term": faTerm(item?.yearOfFundApplication),
        "Registration No": item?.registrationNo,
        "Entity Name": item.entityName,
        State: item?.state,
        // Division: item?.division,
        "Forest Division": item?.allocateDivision,
        "Net Planned Area (Ha)": item?.totalArea,
        "Eco-Restoration Cost (₹)": item?.ecoRestorationAmount,
        "Est.Advance Work Cost (₹)": item?.estimatedAdvanceWorkCost,
        "Est.Raising Cost (₹)": item?.estimatedRaisingCost,
        "Est.Maintenance Cost (₹)": item?.estimatedMaintenanceCost,
        "Est.Monitoring Cost (₹)": item?.estimatedMonitoringCost,
        "Est.Contingency Cost (₹)": item?.estimatedContingencyCost,
        "Est.Total Cost (₹)": item?.totalEstimatedCost,
        "Advance Work Cost (₹)": item?.advanceWorkCost,
        "Raising Cost (₹)": item?.raisingCost,
        "Maintenance Cost (₹)": item?.maintenanceCost,
        "Monitoring Cost (₹)": item?.monitoringCost,
        "Contingency Cost (₹)": item?.contingencyCost,
        "Total Cost (₹)": item?.totalCost,
        "Total Amount Released (₹)": item?.totalAmountReleased,
        "Total Balance (₹)": item?.totalBalance,
        "Created At": item?.created_at,
        "Updated At": item?.updated_at,
        "Financial Year": item?.releaseFinancialYear,
        Remark: item?.remarks,
      }));
      downloadSheet(exportData, generateExportFilename("fund-application"));
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSort = (sortByField) => {
    setPaginationModel({ pageSize: 50, page: 0 });
    setQueryParams({
      ...queryParams,
      offset: 0,
      sortBy: `${sortByField[0]?.field}:${sortByField[0]?.sort.toUpperCase()}`,
    });
  };

  const handlePaginationChange = (newPaginationModel: any) => {
    setPaginationModel(newPaginationModel);
    const newOffset = newPaginationModel.page * newPaginationModel.pageSize;
    setQueryParams((prev) => ({
      ...prev,
      offset: newOffset,
      limit: newPaginationModel.pageSize,
    }));
  };

  return (
    <DashboardWrapper
      title="Fund Application (FA)"
      buttonIcon="/assets/svg/roundPlusIcon.svg"
      buttonText=""
      onButtonClick={() => {}}
      showBtn={!!tableRows.length}
    >
      <>
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
              padding: "12px 1%",
              background: "white",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            }}
          >
            <SearchBar
              getText={(e) => {
                const next = `${e}`;
                // Ignore the repeated same-value calls SearchBar fires on every
                // render; only act (and reset pagination) on a real change.
                if (lastSearchRef.current === next) return;
                lastSearchRef.current = next;
                setPaginationModel({ pageSize: 50, page: 0 });
                setQueryParams((prev) => ({
                  ...prev,
                  offset: 0,
                  searchText: next,
                }));
              }}
              defaultValue={persisted?.queryParams?.searchText || ""}
              customStyles={{
                wrapper: {
                  flex: 1,
                  minWidth: "200px",
                  "& .MuiInputBase-root": toolbarFieldStyles,
                },
              }}
            />
            <MultiSelect
              options={states}
              value={filterKey.projectStateIds}
              sx={{
                m: 0,
                width: 240,
                flexShrink: 0,
                "& .MuiInputBase-root": toolbarFieldStyles,
                // The outlined Select draws its own fieldset on top of the
                // border the theme already gives every InputBase — drop it so
                // the field shows a single border like the search box.
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& .MuiInputLabel-root": {
                  top: "50%",
                  left: "12px",
                  transform: "translateY(-50%)",
                  fontSize: "14px",
                  fontWeight: 400,
                },
                "& .MuiSelect-select": {
                  padding: "0 24px 0 4px !important",
                  display: "flex",
                  alignItems: "center",
                  fontSize: "14px",
                  // The collapsed field renders each selected MenuItem's content,
                  // checkbox included — hide those so it reads as plain text.
                  "& .MuiCheckbox-root": { display: "none" },
                },
              }}
              onChange={(e: any) => {
                setFilterKey((prevFilter) => {
                  const newState = {
                    ...prevFilter,
                    projectStateIds: e.length ? e : null,
                  };

                  setPaginationModel({ pageSize: 50, page: 0 });
                  setQueryParams((prev) => ({
                    ...prev,
                    offset: 0,
                    projectStateIds: newState?.projectStateIds,
                  }));

                  return newState;
                });
              }}
            />
            <MultiSelect
              options={entityInfo}
              label="Filter By Entity"
              clearable
              value={queryParams.entityIds ?? []}
              sx={{
                m: 0,
                width: 240,
                flexShrink: 0,
                "& .MuiInputBase-root": toolbarFieldStyles,
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& .MuiInputLabel-root": {
                  top: "50%",
                  left: "12px",
                  transform: "translateY(-50%)",
                  fontSize: "14px",
                  fontWeight: 400,
                },
                "& .MuiSelect-select": {
                  padding: "0 24px 0 4px !important",
                  display: "flex",
                  alignItems: "center",
                  fontSize: "14px",
                },
              }}
              onChange={(entityIds: string[]) => {
                setPaginationModel({ pageSize: 50, page: 0 });
                setQueryParams((prev) => ({
                  ...prev,
                  offset: 0,
                  entityIds: entityIds.length ? entityIds : undefined,
                }));
              }}
            />
            <Button
              onClick={() => navigate(path.archivedFundApplications)}
              text={"View Archived"}
              variant="outlined"
              customStyles={{ root: toolbarButtonStyles }}
            />
            <Button
              onClick={exportToExcelEntity}
              text={"Export"}
              disabled={isExporting}
              customStyles={{ root: toolbarButtonStyles }}
            />
          </Box>
          {/* FA Year Tabs */}
          <Box
            sx={{
              padding: "0 1%",
              background: "white",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <HorizontalTabs
              tabHeaders={fundYearTabs.map((t) => ({
                label: t.label,
                value: t.value !== undefined ? String(t.value) : "",
              }))}
              tabIndex={activeYearTab !== undefined ? activeYearTab : 0}
              onChange={(val) => {
                const year = val ? Number(val) : undefined;
                setActiveYearTab(year);
                setPaginationModel({ pageSize: 50, page: 0 });
                setQueryParams((prev) => ({
                  ...prev,
                  offset: 0,
                  yearOfFundApplication: year,
                }));
              }}
              components={fundYearTabs.map(() => (
                <></>
              ))}
            />
          </Box>
          <Box
            sx={{
              padding: "10px 0 0 0.5%",
              background: "white",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
              width: "100%",
              overflow: "hidden",
            }}
          >
            <HorizontalTabs
              tabHeaders={fundPaymentTabs}
              tabIndex={Math.max(
                0,
                fundPaymentTabs.findIndex((item) => item.value === activeTab)
              )}
              onChange={(e) => {
                setActiveTab(e);
                navigate(`${path.fundTransfer}${e ? `?currentTab=${e}` : ""}`);
                setPaginationModel({ pageSize: 50, page: 0 });
                setQueryParams((prev) => ({
                  ...prev,
                  offset: 0,
                  status: e,
                }));
              }}
              components={[<></>, <></>]}
              // Squeeze every status tab onto one screen: left-packed, and instead
              // of scrolling sideways the labels shrink and wrap onto two lines.
              customStyles={{
                flexContainer: {
                  minHeight: "auto",
                  "& .MuiTabs-flexContainer": {
                    width: "100%",
                    alignItems: "stretch",
                  },
                  "& .MuiTabs-scrollableX": {
                    overflowX: "hidden",
                  },
                },
                tab: {
                  flex: "0 1 auto",
                  minWidth: "60px",
                  maxWidth: "none",
                  marginRight: 0,
                  padding: "6px 10px",
                  minHeight: "48px",
                  fontSize: "1rem",
                  fontWeight: 500,
                  lineHeight: 1.25,
                  whiteSpace: "normal",
                  textAlign: "center",
                },
              }}
            />
          </Box>
          {/* Data Grid */}
          {isLoading || isFetching ? (
            <HomeTableSkeleton showTabs={false} columns={8} />
          ) : tableRows.length > 0 ? (
            <DataGrid
              rows={tableRows}
              columns={tableColumns}
              rowCount={totalRecords}
              page={paginationModel.page}
              pageSize={paginationModel.pageSize}
              onPageChange={(newPage) =>
                handlePaginationChange({ ...paginationModel, page: newPage })
              }
              onPageSizeChange={(newPageSize) =>
                handlePaginationChange({
                  ...paginationModel,
                  pageSize: newPageSize,
                })
              }
              rowsPerPageOptions={[50]}
              sortingMode="server"
              onSortModelChange={handleSort}
              paginationMode="server"
              columnBuffer={20}
              loading={isLoading || isFetching}
              getRowId={(row) => `${row.id}-${row.userName}`}
            />
          ) : (
            <EmptyView
              heading="No Entry Found"
              subheading=""
              onClick={() => {}}
            />
          )}
        </Box>

        {/* Archive & delete confirmation. Moves the FA (and its dependent rows)
            to the archive tables, then removes it from the live list. */}
        <Dialog
          open={!!archiveRow}
          onClose={() => {
            if (!isArchiving) setArchiveRow(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600 }}>
            Archive & delete fund application
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will archive fund application{" "}
              <strong>{archiveRow?.id}</strong> and remove it from the list.
              This action cannot be undone from here.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              text="Cancel"
              onClick={() => setArchiveRow(null)}
              disabled={isArchiving}
            />
            <Button
              text={isArchiving ? "Archiving..." : "Archive & Delete"}
              onClick={() => archiveFa(archiveRow?.id)}
              disabled={isArchiving}
              customStyles={{
                root: {
                  bgcolor: "error.main",
                  color: "white",
                  "&:hover": { bgcolor: "error.dark" },
                },
              }}
            />
          </DialogActions>
        </Dialog>
      </>
    </DashboardWrapper>
  );
};

export default FundApplication;
